'use client';

import type { RefObject } from 'react';

import RichText from '@/components/ui/RichText/RichText';

import type {
  AutocompleteSuggestion,
  DoctorSuggestion,
  SpecialitySuggestion,
} from '../types';
import DoctorsColumn from './DoctorsColumn';
import SpecialitiesColumn from './SpecialitiesColumn';

interface SearchResultsDropdownProps {
  searchTerm: string;
  suggestions: AutocompleteSuggestion[];
  isLoading: boolean;
  isCodeSearch: boolean;
  activeIndex: number;
  hoveredSpecialitySlug: string | null;
  dropdownRef: RefObject<HTMLDivElement | null>;
  onHoverSpeciality: (slug: string, flatIndex: number) => void;
  onHoverRow: (flatIndex: number) => void;
  onClearHover: () => void;
  onSelect: (url: string) => void;
}

/**
 * Autocomplete listbox rendered below the search bar: specialities |
 * specialists/consultants. CCSD code searches show only the
 * consultants column.
 */
export default function SearchResultsDropdown({
  searchTerm,
  suggestions,
  isLoading,
  isCodeSearch,
  activeIndex,
  hoveredSpecialitySlug,
  dropdownRef,
  onHoverSpeciality,
  onHoverRow,
  onClearHover,
  onSelect,
}: SearchResultsDropdownProps) {
  const colSpanClass = isCodeSearch ? 'md:col-span-1' : 'md:col-span-2';

  // Flat indices stay stable across columns so keyboard navigation,
  // aria-activedescendant and hover highlighting share one coordinate space.
  const specialityItems: { item: SpecialitySuggestion; flatIndex: number }[] =
    [];
  const doctors: DoctorSuggestion[] = [];
  const flatIndexBySlug = new Map<string, number>();
  suggestions.forEach((item, flatIndex) => {
    if (item.type === 'speciality') specialityItems.push({ item, flatIndex });
    else if (item.type === 'doctor') {
      doctors.push(item);
      flatIndexBySlug.set(`doctor:${item.slug}`, flatIndex);
    }
  });

  return (
    <div
      ref={dropdownRef}
      data-testid="search-autocomplete-dropdown"
      id="search-autocomplete-listbox"
      role="listbox"
      className={`border-border-subtle max-h-searchbar-dropdown-max-height md:max-h-searchbar-dropdown-desktop-max-height absolute top-full right-0 left-0 z-9999 mt-2 flex flex-col divide-y divide-slate-100 overflow-y-auto rounded-2xl border bg-white/95 shadow-xl backdrop-blur-md md:grid md:divide-x md:divide-y-0 md:overflow-hidden ${
        isCodeSearch ? 'md:grid-cols-1' : 'md:grid-cols-2'
      }`}
    >
      {isLoading ? (
        <div
          className={`col-span-1 ${colSpanClass} flex items-center justify-center gap-3 py-12`}
        >
          <div className="border-theme-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"></div>
          <RichText styles={{ p: 'text-sm font-medium text-slate-500' }}>
            {'Searching...'}
          </RichText>
        </div>
      ) : suggestions.length === 0 ? (
        <div
          className={`col-span-1 ${colSpanClass} px-4 py-12 text-center text-sm font-medium text-slate-500`}
        >
          No results found for &ldquo;
          <span className="font-bold text-slate-800">{searchTerm}</span>
          &rdquo;
        </div>
      ) : (
        <>
          {/* Left Column: Specialities (hidden for CCSD code searches) */}
          {!isCodeSearch && (
            <SpecialitiesColumn
              items={specialityItems}
              activeIndex={activeIndex}
              hoveredSpecialitySlug={hoveredSpecialitySlug}
              onHover={onHoverSpeciality}
              onClearHover={onClearHover}
              onSelect={onSelect}
            />
          )}

          {/* Middle Column: Consultants/Specialists */}
          <DoctorsColumn
            doctors={doctors}
            activeIndex={activeIndex}
            flatIndexBySlug={flatIndexBySlug}
            hoveredSpecialitySlug={isCodeSearch ? null : hoveredSpecialitySlug}
            isCodeSearch={isCodeSearch}
            onHoverDoctor={onHoverRow}
            onClearHover={onClearHover}
            onSelect={onSelect}
          />
        </>
      )}
    </div>
  );
}
